/**
 * Implementation Checklist Component - Fiche de suivi pour l'emploi des mesures
 * Strategic Dashboard View
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Activity,
  CheckSquare
} from 'lucide-react';

// Import API client
import api from '../../../api/apiClient';

// Import new components
import GlobalUnifiedChecklist from './GlobalUnifiedChecklist';
import ScenarioSidebar from './ScenarioSidebar';

const ImplementationChecklist = ({
  analysisId,
  loading: externalLoading = false
}) => {
  const { t } = useTranslation();

  // State management
  const [workshop5DataFromDB, setWorkshop5DataFromDB] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null); // Null = Global View

  // Load Workshop 5 data
  useEffect(() => {
    if (analysisId) {
      loadWorkshop5Data();
    }
  }, [analysisId]);

  const loadWorkshop5Data = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/analyses/${analysisId}/workshop5-checklist`);

      if (response.success) {
        setWorkshop5DataFromDB(response.data);
      } else {
        throw new Error(response.message || 'Error loading workshop 5 data');
      }
    } catch (err) {
      console.error('Error loading workshop 5 data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Extract scenarios from workshop5DataFromDB
  const scenarios = workshop5DataFromDB?.strategicScenarios || [];

  // Calculate unique responsibles and their metrics
  const responsiblePersons = useMemo(() => {
    const personsMap = new Map();
    const notAssignedKey = t('implementationChecklist.notAssigned');

    scenarios.forEach(scenario => {
      const allMeasures = [
        ...(scenario.customMeasures || []),
        ...(scenario.dreadedEventMeasures || []),
        ...(scenario.ecosystemMeasures || [])
      ].filter(m => m.retained !== false);

      allMeasures.forEach(m => {
        // Normalize responsible person value - check for AI-generated "Non affecté" or "Not assigned"
        const rawPerson = m.responsiblePerson;
        const isUnassigned = !rawPerson ||
          rawPerson === 'Non affecté' ||
          rawPerson === 'Not assigned' ||
          rawPerson === 'Unassigned';
        const person = isUnassigned ? notAssignedKey : rawPerson;
        
        if (!personsMap.has(person)) {
          personsMap.set(person, {
            id: person,
            name: person,
            totalMeasures: 0,
            implementedMeasures: 0,
            riskReduction: 0,
            allMeasures: []
          });
        }

        const stats = personsMap.get(person);
        stats.totalMeasures++;
        stats.allMeasures.push(m);
        if (m.implementation === 'implemented' || m.decision === 'implemented') {
          stats.implementedMeasures++;
        }
      });
    });

    return Array.from(personsMap.values()).map(p => ({
      ...p,
      advancementMetrics: {
        advancementScore: p.totalMeasures > 0 ? Math.round((p.implementedMeasures / p.totalMeasures) * 100) : 0,
        totalMeasures: p.totalMeasures,
        implementedMeasures: p.implementedMeasures,
        riskReduction: 0
      }
    })).sort((a, b) => {
      if (a.name === notAssignedKey) return 1;
      if (b.name === notAssignedKey) return -1;
      return a.name.localeCompare(b.name);
    });
  }, [scenarios, t]);

  const [selectedResponsible, setSelectedResponsible] = useState(null);

  // Loading state
  if (loading || externalLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">{t('workshop5.checklist.loadingWorkshop5')}</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 bg-white min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="text-lg font-semibold text-red-900">{t('common.error')}</h3>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
          <button
            onClick={loadWorkshop5Data}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Main Content Area - Split View Layout */}
      <div className="flex-1 flex overflow-hidden">

        {/* LEFT SIDEBAR: Strategic Navigation */}
        <div className="w-64 flex-shrink-0 bg-white shadow-xl z-20 h-full overflow-hidden border-r border-gray-200">
          <ScenarioSidebar
            scenarios={responsiblePersons}
            selectedScenario={selectedResponsible}
            onSelectScenario={setSelectedResponsible}
            isResponsibleMode={true}
          />
        </div>

        {/* RIGHT CONTENT: Operational Workspace */}
        <div className="flex-1 overflow-y-auto bg-gray-50 relative custom-scrollbar">

          <div className="relative z-10 p-4 max-w-[1600px] mx-auto min-h-full pb-10">
            <GlobalUnifiedChecklist
              analysisId={analysisId}
              selectedResponsible={selectedResponsible}
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default ImplementationChecklist;
